import { FilterArrayType } from 'common/components/Filters';
import { OrderDirection, SortOrder } from 'common/utils/commonTypes';
import { UserApprovalsOrderBy } from '../../common/types/globalTypes';
import { ToNullable } from 'utility/types';
import { UserApprovalSearchFilterTotalsVariables } from './__generated__/UserApprovalSearchFilterTotals';

type OrderLookup = {
  [direction in OrderDirection]: UserApprovalsOrderBy[];
};

const orderByMap = new Map<string, OrderLookup>([
  [
    'name',
    {
      [OrderDirection.ASC]: ['DESCRIPTION_1_ASC'],
      [OrderDirection.DESC]: ['DESCRIPTION_1_DESC'],
    },
  ],
  [
    'number',
    {
      [OrderDirection.ASC]: ['DESCRIPTION_3_ASC'],
      [OrderDirection.DESC]: ['DESCRIPTION_3_DESC'],
    },
  ],
  [
    'description',
    {
      [OrderDirection.ASC]: ['DESCRIPTION_2_ASC'],
      [OrderDirection.DESC]: ['DESCRIPTION_2_DESC'],
    },
  ],
  [
    'date',
    {
      [OrderDirection.ASC]: ['DATE_1_ASC'],
      [OrderDirection.DESC]: ['DATE_1_DESC'],
    },
  ],
  [
    'departmentName',
    {
      [OrderDirection.ASC]: [
        'DEPARTMENT_BY_DEPARTMENT_ID__NAME_ASC',
      ],
      [OrderDirection.DESC]: [
        'DEPARTMENT_BY_DEPARTMENT_ID__NAME_DESC',
      ],
    },
  ],
  [
    'businessUnitName',
    {
      [OrderDirection.ASC]: [
        'BUSINESS_UNIT_BY_BUSINESS_UNIT_ID__NAME_ASC',
      ],
      [OrderDirection.DESC]: [
        'BUSINESS_UNIT_BY_BUSINESS_UNIT_ID__NAME_DESC',
      ],
    },
  ],
  [
    'periodYear',
    {
      [OrderDirection.ASC]: ['_PERIOD_YEAR_ASC'],
      [OrderDirection.DESC]: ['_PERIOD_YEAR_DESC'],
    },
  ],
  [
    'amount',
    {
      [OrderDirection.ASC]: ['AMOUNT_1_ASC'],
      [OrderDirection.DESC]: ['AMOUNT_1_DESC'],
    },
  ],
  [
    'currencyData',
    {
      [OrderDirection.ASC]: ['CURRENCY_ISO_CODE_ASC'],
      [OrderDirection.DESC]: ['CURRENCY_ISO_CODE_DESC'],
    },
  ],
  [
    'approvalGroup',
    {
      [OrderDirection.ASC]: ['APPROVAL_GROUP_ASC'],
      [OrderDirection.DESC]: ['APPROVAL_GROUP_DESC'],
    },
  ],
  [
    'role',
    {
      [OrderDirection.ASC]: ['APPROVAL_PERSONA_ASC'],
      [OrderDirection.DESC]: ['APPROVAL_PERSONA_DESC'],
    },
  ],
  [
    'requesterName',
    {
      [OrderDirection.ASC]: ['REQUESTER_NAME_ASC'],
      [OrderDirection.DESC]: ['REQUESTER_NAME_DESC'],
    },
  ],
  [
    'requesterDate',
    {
      [OrderDirection.ASC]: ['REQUESTED_DATE_ASC'],
      [OrderDirection.DESC]: ['REQUESTED_DATE_DESC'],
    },
  ],
  [
    'approvers',
    {
      [OrderDirection.ASC]: [
        'APPROVAL_HISTORY_APPROVER_BY_ID__APPROVERS_ASC',
      ],
      [OrderDirection.DESC]: [
        'APPROVAL_HISTORY_APPROVER_BY_ID__APPROVERS_DESC',
      ],
    },
  ],
  [
    'approvedDate',
    {
      [OrderDirection.ASC]: ['APPROVED_DATE_ASC'],
      [OrderDirection.DESC]: ['APPROVED_DATE_DESC'],
    },
  ],
  [
    'rejectors',
    {
      [OrderDirection.ASC]: [
        'APPROVAL_HISTORY_APPROVER_BY_ID__REJECTORS_ASC',
      ],
      [OrderDirection.DESC]: [
        'APPROVAL_HISTORY_APPROVER_BY_ID__REJECTORS_DESC',
      ],
    },
  ],
  [
    'rejectedDate',
    {
      [OrderDirection.ASC]: ['REJECTED_DATE_ASC'],
      [OrderDirection.DESC]: ['REJECTED_DATE_DESC'],
    },
  ],
  [
    'amendedByName',
    {
      [OrderDirection.ASC]: [
        'APPROVAL_HISTORY_APPROVER_BY_ID__AMENDED_BY_NAME_ASC',
      ],
      [OrderDirection.DESC]: [
        'APPROVAL_HISTORY_APPROVER_BY_ID__AMENDED_BY_NAME_DESC',
      ],
    },
  ],
  [
    'amendedDate',
    {
      [OrderDirection.ASC]: [
        'APPROVAL_HISTORY_APPROVER_BY_ID__AMENDED_DATE_ASC',
      ],
      [OrderDirection.DESC]: [
        'APPROVAL_HISTORY_APPROVER_BY_ID__AMENDED_DATE_DESC',
      ],
    },
  ],
  [
    'additionalApprovalInformation',
    {
      [OrderDirection.ASC]: [
        'ADDITIONAL_APPROVAL_INFORMATION_ASC',
      ],
      [OrderDirection.DESC]: [
        'ADDITIONAL_APPROVAL_INFORMATION_DESC',
      ],
    },
  ],
  [
    'phaseName',
    {
      [OrderDirection.ASC]: [
        'APPROVAL_PHASE_BY_ID__PHASE_NAME_ASC',
      ],
      [OrderDirection.DESC]: [
        'APPROVAL_PHASE_BY_ID__PHASE_NAME_DESC',
      ],
    },
  ],
  [
    'urgencyLevel',
    {
      [OrderDirection.ASC]: ['URGENCY_LEVEL_ASC'],
      [OrderDirection.DESC]: ['URGENCY_LEVEL_DESC'],
    },
  ],
]);

export const toOrderByVariable = (
  sort?: SortOrder,
  historyFilter: boolean = false
): UserApprovalsOrderBy[] => {
  const orderByArray: UserApprovalsOrderBy[] = historyFilter
    ? ['DESCRIPTION_1_ASC', 'SORT_ORDER_GROUP_DESC', 'PRIMARY_KEY_ASC']
    : [
        'URGENCY_LEVEL_ASC',
        'DESCRIPTION_1_ASC',
        'SORT_ORDER_GROUP_DESC',
        'PRIMARY_KEY_ASC',
      ];

  if (!sort) return orderByArray;

  const orderBy = orderByMap.get(sort.column)?.[sort.direction];

  return [...(orderBy ?? []), 'SORT_ORDER_GROUP_DESC', 'PRIMARY_KEY_ASC'];
};


export const toApprovalFilterVariable = (filterList: FilterArrayType[]) =>
  filterList.map((filter) => {
    switch (filter.filterKey) {
      case 'businessUnitId':
      case 'departmentId':
      case 'corporatePeriodId':
      case 'iconTypeId':
        return {
          [filter.filterKey]: {
            equalTo: filter.value,
          },
        };

      default:
        return undefined;
    }
  });

type searchTotalFilterType = ToNullable<
  Omit<
    UserApprovalSearchFilterTotalsVariables,
    | 'isSigningSearch'
    | 'isHistorySearch'
    | 'isLinkSearch'
    | 'isSearchAllData'
    | 'searchRequest'
    | 'isQueuedSearch'
  >
>;
export const getFilterParams = (searchFilters: FilterArrayType[]) => {
  const searchTotalFilterArray: searchTotalFilterType = {
    filterBusinessUnitId: undefined,
    filterDepartmentId: undefined,
    filterStatusTypeId: undefined,
  };
  searchFilters.forEach((curr) => {
    switch (curr.filterKey) {
      case 'businessUnitId':
        return (searchTotalFilterArray['filterBusinessUnitId'] =
          curr.value as GqlUUID | null);
      case 'departmentId':
        return (searchTotalFilterArray['filterDepartmentId'] =
          curr.value as GqlUUID | null);
      case '_statusTypeId':
        return (searchTotalFilterArray['filterStatusTypeId'] = curr.value as
          | number
          | null);

      default:
        return searchTotalFilterArray;
    }
  });
  return searchTotalFilterArray;
};
